import { useMemo } from 'react';
import { useAuthContext } from 'contexts/AuthContext';

// ============================================================================
// The financial year selected in the header, with its date range. The server
// posts a document only when its date falls inside this year (FinancialYearGuard)
// and refuses a closed year; FyDateInput uses this to offer only valid dates.
// ============================================================================

export interface SelectedFinancialYear {
  id: string;
  name: string;
  /** yyyy-mm-dd */
  startDate: string;
  /** yyyy-mm-dd */
  endDate: string;
  isClosed: boolean;
}

const ymd = (s?: string | null) => (s ? s.slice(0, 10) : '');
export const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** The default date of a new document: today when it falls in the year, else the year's last day (past year) or first day (future year). */
export const defaultDateIn = (fy: SelectedFinancialYear | null): string => {
  const today = todayYmd();
  if (!fy) return today;
  if (today < fy.startDate) return fy.startDate;
  if (today > fy.endDate) return fy.endDate;
  return today;
};

/** Whether a yyyy-mm-dd date falls in the year (true when no year is known). */
export const isDateInYear = (date: string | null | undefined, fy: SelectedFinancialYear | null): boolean =>
  !date || !fy || (date >= fy.startDate && date <= fy.endDate);

export const useSelectedFinancialYear = (): SelectedFinancialYear | null => {
  const { selectedFinancialYear, accessibleFinancialYears } = useAuthContext();
  return useMemo(() => {
    const fy = accessibleFinancialYears.find(f => f.id === selectedFinancialYear?.id);
    if (!fy || !fy.startDate || !fy.endDate) return null;
    return {
      id: fy.id,
      name: fy.fyName || fy.fyCode,
      startDate: ymd(fy.startDate),
      endDate: ymd(fy.endDate),
      isClosed: !!fy.isClosed,
    };
  }, [selectedFinancialYear, accessibleFinancialYears]);
};
